import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  addEntity,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingQuoteCatchmentWorkService } from './generated/billing-quote-catchment-work/billing-quote-catchment-work.service';
import type {
  PatchedQuoteCatchmentWorkUpdateRequestV1Request,
  QuoteCatchmentWorkCreateRequestV1Request,
  QuoteCatchmentWorkV1,
} from './generated/types';

/**
 * Draft catchment works of ONE quote (sub-resource `/quotes/{identity}/catchment_works/`).
 * Same shape as `SetupChargeLinesStore`: the cursor `Query` is the PARENT identity,
 * `load(quoteIdentity)`. Unlike the lines, every mutation answers with the work itself,
 * not the quote: works carry no money, so the quote totals do not move.
 *
 * ponytail: `loadMore` comes free with the feature and has no caller — a quote
 * holds a handful of works, one page covers it.
 */
export const QuoteCatchmentWorksStore = signalStore(
  withEntities<QuoteCatchmentWorkV1>(),
  withCursorPagination<QuoteCatchmentWorkV1, string>(() => {
    const api = inject(BillingQuoteCatchmentWorkService);
    // The generated response IS `CursorPage<QuoteCatchmentWorkV1>`.
    return (identity, cursor) =>
      api.billingV1QuoteCatchmentWorksList(
        { identity },
        { cursor: cursor ?? undefined },
      );
  }),
  withMethods((store, api = inject(BillingQuoteCatchmentWorkService)) => ({
    createWork(identity: string, body: QuoteCatchmentWorkCreateRequestV1Request) {
      return api.billingV1QuoteCatchmentWorksCreate({ identity }, body).pipe(
        map((res) => res.quote_catchment_work),
        tap((work) => {
          patchState(store, addEntity(work));
        }),
      );
    },
    updateWork(
      identity: string,
      workIdentity: string,
      body: PatchedQuoteCatchmentWorkUpdateRequestV1Request,
    ) {
      return api
        .billingV1QuoteCatchmentWorksPartialUpdate({ identity, workIdentity }, body)
        .pipe(
          map((res) => res.quote_catchment_work),
          tap((work) => {
            patchState(store, updateEntity({ id: work.id, changes: work }));
          }),
        );
    },
    /** A work with lines is a 409 (`quote-catchment-work-in-use`): the global
     *  error toast shows the backend's detail and the row stays. */
    deleteWork(identity: string, work: QuoteCatchmentWorkV1) {
      return api
        .billingV1QuoteCatchmentWorksDestroy({ identity, workIdentity: work.identity })
        .pipe(
          tap(() => {
            patchState(store, removeEntity(work.id));
          }),
        );
    },
  })),
);
